/**
 * constants/incrementType.ts
 * IncrementType domain constants and metadata
 *
 * Provides runtime constants for the IncrementType domain including:
 * - Comprehensive metadata map with display names and raw value display rules
 *
 * These constants correspond to the pure type definitions in `src/types/shared/IncrementType.ts`.
 *
 * DOMAIN SEMANTICS:
 * - "cent" and "penny" are distinct: "cent" is USD-specific (stocks, bonds), "penny" is generic
 * - "cent" displays raw values ($0.01) while "penny" shows unit format (1 penny = $0.01)
 * - This distinction prevents conflation in formatting logic downstream
 *
 * ARCHITECTURAL CONSTRAINTS:
 * - Zero cross-domain imports (only `import type` from `types/`)
 * - This module must not import from `utils/` or other runtime logic (constants are side-effect free)
 * - No side effects, pure data only
 * - All exports use `as const satisfies` for compile-time type safety
 *
 * TESTING GUIDELINES:
 * - Do NOT import from this file in tests to assert behavior
 * - Prefer importing parametric utils and passing these constants as inputs
 * - This keeps tests decoupled from constants and focused on logic
 */

// Internal absolute imports (type-only)
import type {
  IncrementType,
  IncrementTypeMeta
} from '@/types/shared/IncrementType';

/**
 * Comprehensive metadata map for all increment types
 *
 * Provides display names and raw value display flags for each IncrementType.
 * The `showRawValue` flag determines whether to display values as raw currency
 * (e.g., "$0.01" for cent) or with unit labels (e.g., "1 tick = $0.25").
 *
 * Structure:
 * - 8 increment types (alphabetically sorted by key)
 * - Each entry has display name and showRawValue boolean
 *
 * Immutable via 'as const satisfies' assertion
 *
 * NOTE: If you change keys, update tests/consumers; order is intentional for stable diffs.
 */
export const INCREMENT_TYPE_META = {
  // keep alphabetical by key
  cent: { display: 'Cent', showRawValue: true },
  dollar: { display: 'Dollar', showRawValue: false },
  increment: { display: 'Increment', showRawValue: true },
  penny: { display: 'Penny', showRawValue: false },
  pip: { display: 'Pip', showRawValue: false },
  pipette: { display: 'Pipette', showRawValue: false },
  point: { display: 'Point', showRawValue: false },
  tick: { display: 'Tick', showRawValue: false }
} as const satisfies Readonly<Record<IncrementType, IncrementTypeMeta>>;

/**
 * Compile-time exhaustiveness check ensuring all IncrementType values are covered
 *
 * This type reduces to `true` when all IncrementType union members exist as keys
 * in INCREMENT_TYPE_META. If a new IncrementType is added to the union but not
 * to the meta map, this type will fail compilation.
 */
type _AllIncrementTypesCovered =
  Exclude<IncrementType, keyof typeof INCREMENT_TYPE_META> extends never ? true : never;

/**
 * @internal
 * Compile-time coverage checks only - bundled export to eliminate unused variable warnings
 * while maintaining drift protection. Do NOT re-export from barrels.
 */
export type _IncrementTypeChecks = _AllIncrementTypesCovered;
